import { WizConstants } from '../../model/constants';
import { DATA_KIND } from '../../model/enums';
import { FreeDataVO } from '../../vo/core/freedata.vo';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { FreedataArgsVO } from '../../vo/core/freedataargs.vo';
import { Model } from '../../model/model';
import { FreeData, FreeDataArgs, FreeDataValue } from '@wizeoo/akm-message-lib';


/**
 * gestion des FreeDatas pour le moteur
 */
export class FreedataEngine {
 
    protected _model: Model = null;
    constructor(model: Model) {
      this._model = model;
    }
    
    public newFreedataVO(source?: FreeData): FreeDataVO {
        return new FreeDataVO(this._model, source);
    }

    public newFreedataValueVO(source?: FreeDataValue): FreedataValueVO {
        return new FreedataValueVO(this._model, source);
    }

    public newFreedataArgsVO(source?: FreeDataArgs): FreedataArgsVO {
        return new FreedataArgsVO(this._model, source);
    }   

    public getFreeDataCollection(): FreeDataVO[] {
        return this._model.freeDataCollection;
    }
    public setFreeDataCollection(value: FreeDataVO[]) {
        this._model.freeDataCollection = value;
    }


    public getFreedataMap(): Map<string, FreeDataVO> {
        return this._model.freedataMap;
    }
    public setFreedataMap(value: Map<string, FreeDataVO>) {
        this._model.freedataMap = value;
    }

    /**
     * Recherche d'un free data par code
     * @param pCode : Code du free data recherché
     * @return FreeDataVO
     */
    public getFreeData(pCode: string): FreeDataVO {
        return this.getFreedataMap()?.get(pCode);
    }

    /**
     * Return le FDV associé au code demandé. Retourne null si aucune correspondance
     * @param pFdvCode le code du FD
     * @param pCode Le code du FDV
     */
    public getFreedataValue(pFDCode: string, pCode: string): FreedataValueVO {
        const freeData: FreeDataVO = this.getFreeData(pFDCode.split(WizConstants.CONTEXT_SEPARATOR)[0]); // suppression du niveau
        let result: FreedataValueVO = null;
        if (!!freeData) {
            result = freeData.getFreeDataValue(pCode); 
            if (result === undefined) {
                result = null;
            }
            if(!result) {
                // check dans les updates, création de slaveBaseline en cours
                if(freeData.updateMap?.has(pCode)) {
                    result = freeData.updateMap.get(pCode)[0];
                    // console.log('inUpdate', result);
                }
            }
        }
        return result;
    }

    /**
     * Return les FDVs associés au code PARENT demandé. Retourne null si aucune correspondance
     * @param pFdvCode le code du FD
     * @param pCode Le code du FDV PARENT
     */
    public getFreedataValueByParent(pFDCode: string, pCode: string): FreedataValueVO[] {
        const freeData: FreeDataVO = this.getFreeData(pFDCode.split(WizConstants.CONTEXT_SEPARATOR)[0]); // suppression du niveau
        let result: FreedataValueVO[] = null;
        if (!!freeData) {
            result = freeData.getFreeDataValueByParent(pCode);
            if (result === undefined) {
                result = null;
            }
        }
        return result;
    }

    /**
     * Return le FDV associé au shortCode demandé. Retourne null si aucune correspondance ou multiple
     * @param pFdvCode le code du FD
     * @param pCode Le code du FDV
     */
    public getFreedataValueByShortCode(pFDCode: string, pShortCode: string): FreedataValueVO {
        const freeData: FreeDataVO = this.getFreeData(pFDCode.split(WizConstants.CONTEXT_SEPARATOR)[0]); // suppression du niveau
        if (!!freeData) {
            const fdv: FreedataValueVO[] = freeData.getFreeDataValueByShortCode(pShortCode);
            if (fdv && fdv.length === 1) {
                return fdv[0];
            }
        }
        return null;
    }

    /**
     * Alimentation des freedatas du modèle
     * @param freedatas: FreeData[]
     */
    public feedFreedatas(freedatas: FreeData[]): void {
        const result: FreeDataVO[] = freedatas.map((fd) => this.newFreedataVO(fd));

        const map: Map<string, FreeDataVO> = new Map<string, FreeDataVO>();
        result.forEach((fd: FreeDataVO) => {
            map.set(fd.code, fd);
        });

        this._model.freeDataCollection = result;
        this.setFreedataMap(map);
    }

    /**
     * Alimentation des freedatas values dans leurs freedatas correspondantes
     * @param freedatasvalues: FreeDataValue[]
     */
    public feedFreedatasValues(freedatasvalues: FreeDataValue[]) {
        freedatasvalues.forEach((fdv: FreeDataValue) => {
            const fdvvo: FreedataValueVO = this.newFreedataValueVO(fdv);
            const fdvo: FreeDataVO = this.getFreeData(fdvvo.freeDataCode);
            if (!!fdvo) {
                fdvo.addFreeDataValue(fdvvo);
            }
        });
    }




    /**
     * collecte la valeur pour un champ de FDV
     * @param model 
     * @param fdv fdv
     * @param field champ sur le FDV
     * @returns 
     */
    public getFDValue(fdv: FreedataValueVO, field: string): string {
        if(!field) {
            field = WizConstants.FIELD_CODE;
        }
        let result: string = this.getBasicValue(fdv, field);

        if(!result) { // pas dedans, c'est un args
            
            // on split sur @ pour gérer les propriétés sur les sous-objets
            let fieldParts: string[] = field.split(WizConstants.FD_PARTS_SEPARATOR);
            if(!!fdv.trueArgsObj && !!fdv.trueArgsObj[fieldParts[0]]) {
                
                result = fdv.trueArgsObj[fieldParts[0]]; // j'ai un résultat
                
                // mais c'est peut-être sous la forme CODIF#champ
                if(result && fieldParts.length> 1) {
                    let subFDVParts: string[] = result.split(WizConstants.CONTEXT_SEPARATOR); 
                    let subFDV: FreedataValueVO = this.getFreedataValue(subFDVParts[0], subFDVParts[1]);
                    if(subFDV) {
                        result = this.getBasicValue(subFDV, fieldParts[1]); // on prend le résultat de base
                    }
                    if(!result && subFDV.trueArgsObj && subFDV.trueArgsObj[fieldParts[1]]) {
                        result = subFDV.trueArgsObj[fieldParts[1]];
                    }
                   //  console.log('subField', fieldParts);
                } 
            }
        }
        return result;
    }

    /**
     * collecte une mesure sur un FDV
     * @param fdv le fdv cible
     * @param field champ cible
     */
    private getBasicValue(fdv: FreedataValueVO, field): string {
        let result: string = null;
        switch(field) {
            case WizConstants.FIELD_LABEL:
                result = fdv.trsLabel;
                break;
            case WizConstants.FIELD_DESCRIPTION:
                result = fdv.trsDescription;
                break;
            case 'createTime':
                result = fdv.createTime?.toString();
                break;
            case 'updateTime':
                result = fdv.updateTime?.toString();
                break;
            case 'startDate':
                result = fdv.startDate?.toString();
                break;
            case 'endDate':
                result = fdv.endDate?.toString();
                break;
            case 'deprecated':
                result = fdv.deprecated ? '1' : '0';
                break;
            case 'restrict':
                result = fdv.restrict;
                break;
            default:
                result = fdv[field];
                break;
        }
        
        return result;
    }

    /**
     * alimentation des clés / sous clés des FDs
     * @param fd
     * @param fdv
     */
    public feedFDPairs(fd: FreeDataVO, fdv: FreedataValueVO): any {
        const pairs: { [key: string]: string } = {};

        Object.keys(fdv.trueArgsObj).forEach((key: string) => {
            const argvo: FreedataArgsVO = fd.getArgsVOMap().get(key);
            if (argvo && argvo.dataType === DATA_KIND.OBJECT) {
                if (!!fdv.trueArgsObj[key]) {
                    const pair = fdv.trueArgsObj[key].split(WizConstants.CONTEXT_SEPARATOR);
                    const subFDV: FreedataValueVO = this.getFreedataValue(pair[0], pair[1]);
                    if (!!subFDV) {
                        pairs[key] = subFDV.trsLabel;
                        // console.log('subFD OK',key, pairs[key]);
                    } else {
                        // console.log('subFD KO',key, pairs[key]);
                        pairs[key] = fdv.trueArgsObj[key];
                    }
                }
            } else {
                pairs[key] = fdv.trueArgsObj[key];
                // console.log('single',key, pairs[key]);
            }
        });

        this.addBasicPairs(pairs, fdv);
        return pairs;
    }

    /**
     * alimente les pairs de base sur un FDV
     * @param pairs le tableau de pairs à alimenter
     * @param fdv le fdv cible
     */
    public addBasicPairs(pairs: { [key: string]: string }, fdv: FreedataValueVO): void {
        pairs['id'] = fdv.id?.toString();
        pairs['shortCode'] = fdv.shortCode;
        pairs['parentCode'] = fdv.parentCode;
        pairs['label'] = fdv.trsLabel;
        pairs['valor'] = fdv.valor;
        pairs['externalCode'] = fdv.externalCode;
        pairs['externalParentCode'] = fdv.externalParentCode;
        pairs['description'] = fdv.trsDescription;
        pairs['icon'] = fdv.icon;
        pairs['code'] = fdv.code;
        pairs['createBy'] = fdv.createBy;
        pairs['updateBy'] = fdv.updateBy;
        pairs['parentShortCode'] = fdv.parentShortCode;
        pairs['createTime'] = fdv.createTime?.toString();
        pairs['updateTime'] = fdv.updateTime?.toString();
        pairs['startDate'] = fdv.startDate?.toString();
        pairs['endDate'] = fdv.endDate?.toString();
        pairs['deprecated'] = fdv.deprecated ? '1' : '0';
        pairs['restrict'] = fdv.restrict;
        pairs['order'] = fdv.order?.toString();

    }

  /**
   * alimentation de la pile de tags
   * @param tags 
   */
  public feedTags(tags: object): void {
    let userTagMap: Map<string, Set<string>> = this._model.userTagMap || new Map();

    Object.keys(tags || {}).forEach(key => {
      let shortCodes: Set<string> =  new Set(tags[key]); // on a stocké des shortCodes
      let codes: Set<string> = new Set(); // mais on veut des codes 
      let parts: any[] = key.split(WizConstants.CONTEXT_SEPARATOR);

      // le FD cible
      let fd: FreeDataVO = this._model.freedataEngine.getFreeData(parts[0]);
      
      if(fd) {
        // on regarde s'il n'y a pas un arg
        // TODO TODO 
        // let subParts: any[] = parts[1].split(WizConstants.FD_PARTS_SEPARATOR);
        

          let fdvs: FreedataValueVO[];
          [...shortCodes.values() || []].forEach(fdvKey => {
            fdvs = fd.getFreeDataValueByShortCode(fdvKey);
            if(fdvs?.length > 0) {
              fdvs.forEach(fdvo => {
                if(fdvo.level === Number(parts[1]))
                codes.add(fdvo.code);
              });
            }
          });
          userTagMap.set(key, codes);
        }
    });

    this._model.userTagMap = userTagMap;
  }


}
